import { useState, useEffect, useRef } from 'react';
import { X, Send, MessageSquare, Plus, Users } from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';
import { io, Socket } from 'socket.io-client';
import { useAppSelector } from '../../store';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4040';

interface ChatMsg {
  id: string;
  channel: string;
  channelType: string;
  targetUserId?: string;
  groupId?: string;
  from: string;
  fromName: string;
  text: string;
  timestamp: string;
  eventId?: string;
}

interface ChatChannel {
  id: string;
  type: 'command' | 'private' | 'group';
  name: string;
  targetUserId?: string;
  groupId?: string;
}

interface CommandChatPanelProps {
  open: boolean;
  onClose: () => void;
  onUnreadChange?: (count: number) => void;
}

export default function CommandChatPanel({ open, onClose, onUnreadChange }: CommandChatPanelProps) {
  const { user, token } = useAppSelector((state) => state.auth);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [activeChannel, setActiveChannel] = useState<ChatChannel | null>(null);
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [unreadByChannel, setUnreadByChannel] = useState<Record<string, number>>({});
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(open);

  // Connect socket and listen for messages
  useEffect(() => {
    if (!user) return;
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token: localStorage.getItem('c2_token'), userId: user.id },
    });
    socketRef.current = socket;

    socket.on('chat:new_message', (msg: ChatMsg) => {
      setMessages((prev) => [...prev, msg]);
      // Track unread if panel is closed or different channel
      if (msg.from !== user?.id) {
        const channelId = msg.channelType === 'private' ? `private:${msg.targetUserId}` :
                         msg.channelType === 'group' ? `group:${msg.groupId}` : 'command';
        setUnreadByChannel((prev) => ({
          ...prev,
          [channelId]: (prev[channelId] || 0) + 1,
        }));
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id]);

  // Fetch available channels
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/chat/channels`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setChannels(data.channels || []);
        if (!activeChannel && data.channels?.length > 0) {
          setActiveChannel(data.channels[0]);
        }
      })
      .catch(() => {});
  }, [token]);

  // Fetch messages for active channel
  useEffect(() => {
    if (!activeChannel || !token) return;
    const params = new URLSearchParams();
    params.append('type', activeChannel.type);
    if (activeChannel.type === 'private') params.append('targetUserId', activeChannel.targetUserId || '');
    if (activeChannel.type === 'group') params.append('groupId', activeChannel.groupId || '');

    fetch(`${API_BASE_URL}/chat?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setMessages(data.messages || []))
      .catch(() => {});
  }, [activeChannel, token]);

  // Fetch available users for group creation
  useEffect(() => {
    if (!token || !showCreateGroup) return;
    fetch(`${API_BASE_URL}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setAvailableUsers(data.users || []))
      .catch(() => {});
  }, [token, showCreateGroup]);

  // Report total unread count to parent
  useEffect(() => {
    const total = Object.values(unreadByChannel).reduce((a, b) => a + b, 0);
    onUnreadChange?.(total);
  }, [unreadByChannel, onUnreadChange]);

  // Clear unread for active channel when panel is open
  useEffect(() => {
    if (open && activeChannel) {
      setUnreadByChannel((prev) => ({ ...prev, [activeChannel.id]: 0 }));
    }
    wasOpenRef.current = open;
  }, [open, activeChannel]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (open) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open, activeChannel]);

  const sendMessage = () => {
    const text = input.trim();
    if (!text || !socketRef.current || !user || !activeChannel) return;
    socketRef.current.emit('chat:message', {
      channelType: activeChannel.type,
      targetUserId: activeChannel.targetUserId,
      groupId: activeChannel.groupId,
      from: user.id,
      fromName: `${user.firstName} ${user.lastName}`,
      text,
    });
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const channelMessages = messages.filter((m) => {
    if (!activeChannel) return false;
    if (activeChannel.type === 'command') return m.channelType === 'command';
    if (activeChannel.type === 'private') return m.channelType === 'private' && m.targetUserId === activeChannel.targetUserId;
    if (activeChannel.type === 'group') return m.channelType === 'group' && m.groupId === activeChannel.groupId;
    return false;
  });

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || selectedMembers.length === 0 || !token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/chat/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newGroupName.trim(), memberIds: selectedMembers }),
      });
      if (res.ok) {
        setNewGroupName('');
        setSelectedMembers([]);
        setShowCreateGroup(false);
        // Refresh channels
        fetch(`${API_BASE_URL}/chat/channels`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((data) => setChannels(data.channels || []))
          .catch(() => {});
      }
    } catch { /* ignore */ }
  };

  const toggleMember = (userId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-[360px] z-[1000] flex flex-col bg-card border-l border-border shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <span className="text-sm font-bold text-foreground">Messages</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowCreateGroup(true)} className="p-1 rounded hover:bg-accent transition-colors" title="Create group">
            <Plus className="w-4 h-4 text-muted-foreground" />
          </button>
          <button onClick={onClose} className="p-1 rounded hover:bg-accent transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Channel tabs */}
      <div className="flex gap-1 px-3 py-2 border-b border-border flex-shrink-0 overflow-x-auto">
        {channels.map((ch) => {
          const unread = unreadByChannel[ch.id] || 0;
          return (
            <button
              key={ch.id}
              onClick={() => {
                setActiveChannel(ch);
                setUnreadByChannel((prev) => ({ ...prev, [ch.id]: 0 }));
              }}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 capitalize whitespace-nowrap',
                activeChannel?.id === ch.id
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent'
              )}
            >
              {ch.name}
              {unread > 0 && activeChannel?.id !== ch.id && (
                <span className="min-w-[16px] h-4 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center px-1">
                  {unread}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 min-h-0">
        {channelMessages.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-xs">
            No messages in #{activeChannel?.name || 'channel'} yet.
            <br />
            <span className="text-[10px] opacity-60">Messages from MDT and Command users appear here.</span>
          </div>
        )}
        {channelMessages.map((m) => {
          const isMe = m.from === user?.id;
          return (
            <div key={m.id} className={cn('max-w-[85%]', isMe ? 'ml-auto' : 'mr-auto')}>
              <div className={cn(
                'px-3 py-2 text-xs leading-relaxed rounded-lg',
                isMe
                  ? 'bg-primary text-primary-foreground rounded-br-sm'
                  : 'bg-accent text-foreground rounded-bl-sm'
              )}>
                {!isMe && (
                  <div className="text-[10px] font-bold text-primary mb-0.5">{m.fromName}</div>
                )}
                {m.text}
                <div className={cn('text-[9px] mt-1', isMe ? 'text-primary-foreground/50' : 'text-muted-foreground')}>
                  {new Date(m.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2 px-3 py-3 border-t border-border flex-shrink-0">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Message ${activeChannel?.name || 'channel'}...`}
          className="flex-1 px-3 py-2 rounded-md text-xs bg-background border border-border text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-primary/50"
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim()}
          className={cn(
            'w-8 h-8 rounded-md flex items-center justify-center transition-colors',
            input.trim()
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-accent text-muted-foreground'
          )}
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold text-foreground">Create Group</span>
              </div>
              <button onClick={() => setShowCreateGroup(false)} className="p-1 rounded hover:bg-accent transition-colors">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">Group Name</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g. Alpha Team"
                  className="w-full px-3 py-2 rounded-md text-xs bg-background border border-border text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">Members</label>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {availableUsers
                    .filter((u) => u.id !== user?.id)
                    .map((u) => (
                      <button
                        key={u.id}
                        onClick={() => toggleMember(u.id)}
                        className={cn(
                          'w-full px-3 py-2 rounded-md text-xs text-left flex items-center gap-2 transition-colors',
                          selectedMembers.includes(u.id)
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'bg-accent text-foreground border border-border hover:bg-accent/80'
                        )}
                      >
                        <div className={cn(
                          'w-4 h-4 rounded-full flex items-center justify-center border',
                          selectedMembers.includes(u.id) ? 'bg-primary border-primary' : 'border-border'
                        )}>
                          {selectedMembers.includes(u.id) && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <span>{u.firstName} {u.lastName}</span>
                      </button>
                    ))}
                </div>
              </div>
              <button
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim() || selectedMembers.length === 0}
                className={cn(
                  'w-full py-2 rounded-md text-xs font-medium transition-colors',
                  newGroupName.trim() && selectedMembers.length > 0
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'bg-accent text-muted-foreground cursor-not-allowed'
                )}
              >
                Create Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
